import { activeSiteConfig } from "@config/site/site-config";
import { QueryKeys } from "@lib/enums/QueryKeys";
import type { QueryKey } from "@tanstack/react-query";
import type { StoreData } from "@features/stores/types";
import type { SearchResultLocation } from "../controller";
import { filterRestrictedParcelShops } from "./filterRestrictedParcelShops";
import { getParcelShopsByAddress, getParcelShopsByCoordinates } from "./parcelShops";
import { parseSearchResultLocation } from "./parseAddressComponents";

export type ParcelShopSearchMethod = "address" | "latlng";

export type ParcelShopSearch = {
    searchResult?: SearchResultLocation;
    clientLocation?: { lat: number; lng: number };
};

/** Only the markets configured for it search by address; every other one searches by point. */
export const parcelShopSearchMethod = (): ParcelShopSearchMethod =>
    activeSiteConfig?.checkout?.parcelShops?.searchMethod === "address" ? "address" : "latlng";

export const parcelShopsQueryKey = ({
    searchResult,
    clientLocation,
}: ParcelShopSearch): QueryKey =>
    parcelShopSearchMethod() === "address"
        ? [QueryKeys.getParcelShops, JSON.stringify(searchResult)]
        : [QueryKeys.getParcelShops, clientLocation?.lat, clientLocation?.lng];

export const canSearchParcelShops = ({
    searchResult,
    clientLocation,
}: ParcelShopSearch): boolean =>
    parcelShopSearchMethod() === "address"
        ? Boolean(searchResult)
        : Boolean(clientLocation?.lat && clientLocation?.lng);

/**
 * The one parcel-shop fetch. Shared so the checkout panel and the store locator's map land on a
 * single query rather than two that can disagree about what the map is showing.
 */
export const fetchParcelShops = async ({
    searchResult,
    clientLocation,
}: ParcelShopSearch): Promise<StoreData[]> => {
    let shops: StoreData[];

    if (parcelShopSearchMethod() === "address" && searchResult) {
        const address = parseSearchResultLocation(searchResult);
        shops = await getParcelShopsByAddress({
            city: address.city,
            postCode: address.postalCode,
        });
    } else if (clientLocation?.lat && clientLocation?.lng) {
        shops = await getParcelShopsByCoordinates({
            latitude: clientLocation.lat,
            longitude: clientLocation.lng,
        });
    } else {
        return Promise.reject(new Error("Invalid search parameters"));
    }

    return filterRestrictedParcelShops(activeSiteConfig.country, shops);
};
